import { useState } from "react";
import { appConfirm } from "../../../components/ConfirmDialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { CreditCard, Banknote, Undo2, Trash2 } from "lucide-react";
import { money, Stat } from "./shared";

export function Payments({ data, canManage, role, reference, reload }) {
  const [manual, setManual] = useState({ amount: "", method: "bacs", payment_ref: "", note: "" });
  const [cardAmount, setCardAmount] = useState("");
  const [refund, setRefund] = useState({ amount: "", method: "card", reason: "", adminFee: false, send_email: true });
  const [refundBusy, setRefundBusy] = useState(false);

  const processRefund = async () => {
    const amt = parseFloat(refund.amount);
    if (!amt || amt <= 0) { toast.error("Enter a refund amount"); return; }
    const msg = refund.method === "card"
      ? `Refund ${money(amt)} to the client's card via Stripe? This cannot be undone here.`
      : `Record a ${money(amt)} ${refund.method.toUpperCase()} refund? Make sure you have actually sent the money.`;
    if (!(await appConfirm({ title: refund.method === "card" ? "Refund via Stripe?" : "Record manual refund?", description: msg, confirmLabel: "Process refund", destructive: true }))) return;
    setRefundBusy(true);
    try {
      const deductions = refund.adminFee ? [{ label: "Administration fee", amount: 499 }] : [];
      const r = await api.post(`/admin/booking/${reference}/refund`, { amount: amt, method: refund.method, reason: refund.reason, deductions, send_email: refund.send_email });
      toast.success(`Refund of ${money(amt)} processed${r.data.sent ? " · client emailed" : ""}`);
      setRefund({ amount: "", method: "card", reason: "", adminFee: false, send_email: true }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    setRefundBusy(false);
  };

  const recordManual = async () => {
    if (!manual.amount) { toast.error("Enter an amount"); return; }
    try { await api.post(`/admin/booking/${reference}/payment/manual`, manual); toast.success("Payment recorded"); setManual({ amount: "", method: "bacs", payment_ref: "", note: "" }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
  };
  const takeCard = async () => {
    if (!cardAmount) { toast.error("Enter an amount"); return; }
    try {
      const r = await api.post(`/admin/booking/${reference}/payment/checkout`, { amount: parseFloat(cardAmount), origin: window.location.origin });
      window.open(r.data.url, "_blank");
      toast.info("Stripe payment page opened — checking for completion…");
      poll(r.data.session_id, 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create payment"); }
  };
  const poll = async (sid, n) => {
    if (n > 30) return;
    try {
      const r = await api.get(`/admin/payment/status/${sid}`);
      if (r.data.payment_status === "paid") { toast.success("Card payment received"); reload(); return; }
    } catch (e) { console.debug("Card payment poll failed (will retry):", e); }
    setTimeout(() => poll(sid, n + 1), 3000);
  };
  const voidPayment = async (id) => {
    if (!(await appConfirm({ title: "Remove this payment record?", confirmLabel: "Remove", destructive: true }))) return;
    try { await api.delete(`/admin/payment/${id}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot remove"); }
  };
  const confirmPayment = async (id) => {
    try { await api.post(`/admin/payment/${id}/confirm`); toast.success("Payment confirmed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
  };

  const paid = (data.payments || []).filter(p => p.payment_status === "paid");
  const reported = (data.payments || []).filter(p => p.payment_status === "unconfirmed");
  const pending = (data.payments || []).filter(p => p.payment_status !== "paid" && p.payment_status !== "unconfirmed");
  return (
    <div data-testid="jobfile-payments">
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Stat label="Paid to date" value={money(data.paid_to_date)} />
        <Stat label="Outstanding balance" value={money(data.balance)} highlight />
      </div>

      {reported.length > 0 && (
        <div className="border border-[#C79A3B] bg-[#FBF3EC] p-3 mb-4" data-testid="client-reported-payments">
          <div className="font-bold mb-2 text-sm">Client-reported payments — awaiting your confirmation</div>
          {reported.map(p => (
            <div key={p.id} data-testid={`reported-payment-${p.id}`} className="flex items-center justify-between text-sm border-t border-[#EAC7B4] py-2">
              <div>
                <span className="font-bold">{money(p.amount)}</span> · <span className="uppercase text-xs">{p.method}</span>
                {p.payment_ref && <span className="text-xs text-[#6B7280]"> · {p.payment_ref}</span>}
                <div className="text-xs text-[#6B7280]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-GB") : ""}{p.note ? ` · ${p.note}` : ""}{p.reported_by ? ` · ${p.reported_by}` : ""}</div>
              </div>
              {canManage && (
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`payment-confirm-${p.id}`} onClick={() => confirmPayment(p.id)} className="de-btn-primary text-xs py-1">Confirm</button>
                  <button data-testid={`payment-reject-${p.id}`} onClick={() => voidPayment(p.id)} className="de-btn-secondary text-xs py-1">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-[#E5E7EB] bg-white p-4">
            <div className="font-bold mb-2 flex items-center gap-2"><Banknote size={16} /> Record offline payment</div>
            <input data-testid="pay-manual-amount" type="number" value={manual.amount} onChange={e => setManual({ ...manual, amount: e.target.value })} placeholder="Amount £" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
            <select data-testid="pay-manual-method" value={manual.method} onChange={e => setManual({ ...manual, method: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2 bg-white">
              {["bacs", "cash", "cheque", "card_terminal"].map(m => <option key={m} value={m}>{m.replace("_", " ").toUpperCase()}</option>)}
            </select>
            <input data-testid="pay-manual-ref" value={manual.payment_ref} onChange={e => setManual({ ...manual, payment_ref: e.target.value })} placeholder="Reference (optional)" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
            <input data-testid="pay-manual-note" value={manual.note} onChange={e => setManual({ ...manual, note: e.target.value })} placeholder="Note (optional)" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
            <button data-testid="pay-manual-save" onClick={recordManual} className="de-btn-primary w-full justify-center">Record payment</button>
          </div>
          <div className="border border-[#E5E7EB] bg-white p-4">
            <div className="font-bold mb-2 flex items-center gap-2"><CreditCard size={16} /> Take a card payment</div>
            <p className="text-xs text-[#6B7280] mb-2">Opens a secure Stripe page. Pay on this device or send the link to the client.</p>
            <input data-testid="pay-card-amount" type="number" value={cardAmount} onChange={e => setCardAmount(e.target.value)} placeholder="Amount £" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
            <button data-testid="pay-card-go" onClick={takeCard} className="de-btn-secondary w-full justify-center">Create card payment</button>
          </div>
        </div>
      )}

      {role === "senior_admin" && data.paid_to_date > 0 && (
        <div className="border border-[#C79A3B] bg-[#FBF3EC] p-4 mb-6" data-testid="jobfile-refund">
          <div className="font-bold mb-1 flex items-center gap-2"><Undo2 size={16} /> Process a refund</div>
          <p className="text-xs text-[#6B7280] mb-3">Received to date: <b>{money(data.paid_to_date)}</b>. Card refunds go back to the client's card via Stripe; for BACS/cash, record the refund here after you've sent the money. A refund confirmation email (per your T&amp;Cs) is sent automatically.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input data-testid="refund-amount" type="number" value={refund.amount} onChange={e => setRefund({ ...refund, amount: e.target.value })} placeholder="Refund amount £" className="border border-[#E5E7EB] px-3 py-2 text-sm" />
            <select data-testid="refund-method" value={refund.method} onChange={e => setRefund({ ...refund, method: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white">
              <option value="card">Card (auto-refund via Stripe)</option>
              <option value="bacs">BACS (record only)</option>
              <option value="cash">Cash (record only)</option>
              <option value="cheque">Cheque (record only)</option>
            </select>
          </div>
          <input data-testid="refund-reason" value={refund.reason} onChange={e => setRefund({ ...refund, reason: e.target.value })} placeholder="Reason / notes (shown in the email)" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full mb-2" />
          <label className="flex items-center gap-2 text-xs text-[#6B7280] mb-2">
            <input data-testid="refund-adminfee" type="checkbox" checked={refund.adminFee} onChange={e => setRefund({ ...refund, adminFee: e.target.checked })} />
            Note £499 administration fee retained in the email (per T&amp;Cs)
          </label>
          <label className="flex items-center gap-2 text-xs text-[#6B7280] mb-3">
            <input data-testid="refund-email" type="checkbox" checked={refund.send_email} onChange={e => setRefund({ ...refund, send_email: e.target.checked })} />
            Email the client a refund confirmation
          </label>
          <button data-testid="refund-go" disabled={refundBusy} onClick={processRefund} className="bg-[#C79A3B] text-white font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-[#A87E28] disabled:opacity-60 transition-colors">{refundBusy ? "Processing…" : "Process refund"}</button>
        </div>
      )}

      <div className="label-eyebrow mb-2">Payment history</div>
      <div className="space-y-2">
        {paid.map(p => (
          <div key={p.id} data-testid={`payment-${p.id}`} className="border border-[#E5E7EB] bg-white p-3 flex items-center justify-between text-sm">
            <div>
              <span className="font-bold">{money(p.amount)}</span> · <span className="uppercase text-xs">{(p.method || "card")}</span>
              {p.payment_ref && <span className="text-xs text-[#6B7280]"> · {p.payment_ref}</span>}
              <div className="text-xs text-[#6B7280]">{p.paid_at ? new Date(p.paid_at).toLocaleString("en-GB") : ""}{p.recorded_by ? ` · ${p.recorded_by}` : ""}{p.note ? ` · ${p.note}` : ""}</div>
            </div>
            {canManage && p.method !== "card" && <button data-testid={`payment-void-${p.id}`} onClick={() => voidPayment(p.id)} className="text-[#6B7280] hover:text-[#C79A3B]"><Trash2 size={14} /></button>}
          </div>
        ))}
        {pending.map(p => (
          <div key={p.id} className="border border-dashed border-[#E5E7EB] p-3 text-sm text-[#6B7280]">{money(p.amount)} · card · <span className="uppercase text-xs">{p.payment_status}</span></div>
        ))}
        {paid.length === 0 && <div className="text-sm text-[#6B7280]">No payments recorded yet.</div>}
      </div>
    </div>
  );
}
